import { z } from "zod";
import { AiQuota } from "./scan.js";

/** druif's exporter stamps a build as `20060102T150405Z`; the edge serves only that shape as a version. */
export const CATALOGUE_VERSION_PATTERN = /^[0-9]{8}T[0-9]{6}Z$/;

/**
 * The files a published catalogue version may hold (docs/design/pwa-on-cloudflare.md, the delta
 * `delta-<from version>.br` of docs/design/catalogue-delta.md, and the Natural Earth country outlines
 * `outlines-<scale>.json` the PWA draws its locator map from). Schema 3 adds the lazy objects the exporter
 * writes beside a version and a device fetches one at a time: `geo/<cc>.json` (appellation and region
 * shapes of one country), the same split into parts, `geo/<cc>/index.json` and `geo/<cc>/<n>.json`, `contours/<appellationId>.json`, `parcels/<producerId>.json` and
 * `labels/<file>` (a label image). An allowlist, not a path check, so no spelling of `..` or a slash can
 * reach another R2 key. Keep it in step with `lazyNamePattern` in apps/druif/internal/catalogue/lazy.go.
 */
export const CATALOGUE_FILE_PATTERN =
  /^(?:catalogue\.sqlite(?:\.br)?|climate\.json|outlines-(?:110m|50m)\.json|geo\/[a-z]{2}(?:\/(?:index|[0-9]{1,6}))?\.json|contours\/[0-9]{1,12}\.json|parcels\/[0-9]{1,12}\.json|labels\/[A-Za-z0-9][A-Za-z0-9._-]{0,119}\.(?:[Jj][Pp][Ee]?[Gg]|[Pp][Nn][Gg]|[Ww][Ee][Bb][Pp])|delta-[0-9]{8}T[0-9]{6}Z\.br)$/;

/**
 * The compressed transfer size under which a device applies a catalogue increment on its own, in the
 * background, without asking. At or above it (or with no increment to offer) the person is asked and
 * the full bundle is downloaded, as before. The manifest advertises each increment's size, so the device
 * decides without downloading anything.
 */
export const CATALOGUE_SILENT_UPDATE_MAX_BYTES = 5_000_000;

/** One published file as druif's `manifest.json` lists it; `decoded` names what a compressed file expands to. */
export const CatalogueManifestFile = z.object({
  name: z.string().min(1).max(200),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bytes: z.number().int().nonnegative(),
  encoding: z.literal("br").optional(),
  decoded: z.string().min(1).max(200).optional(),
});
export type CatalogueManifestFile = z.infer<typeof CatalogueManifestFile>;

/**
 * One increment from `from` to `to`, kept in the `to` version's directory. `bytes` is the download (the
 * brotli file); `content_sha256` is the digest the device's database must have once the delta is applied.
 */
export const CatalogueManifestDelta = z.object({
  from: z.string().regex(CATALOGUE_VERSION_PATTERN),
  to: z.string().regex(CATALOGUE_VERSION_PATTERN),
  name: z.string().regex(CATALOGUE_FILE_PATTERN),
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  content_sha256: z.string().regex(/^[0-9a-f]{64}$/),
  bytes: z.number().int().nonnegative(),
  decoded_bytes: z.number().int().nonnegative(),
  format: z.number().int().positive(),
});
export type CatalogueManifestDelta = z.infer<typeof CatalogueManifestDelta>;

/** `GET /v1/catalogue/manifest`: what the device reads first to learn which catalogue to fetch and how to check it. */
export const CatalogueManifest = z.object({
  catalogue_version: z.string().regex(CATALOGUE_VERSION_PATTERN),
  built_at: z.iso.datetime(),
  files: z.array(CatalogueManifestFile),
  /** Digest of the catalogue's rows (not its build time): equal digests mean equal data. */
  content_sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
  /**
   * The chain of increments ending at `catalogue_version`, oldest first. Additive: a reader that does not
   * know the field downloads `files` as it always did.
   */
  deltas: z.array(CatalogueManifestDelta).optional(),
  /**
   * How many lazy objects (see CATALOGUE_FILE_PATTERN) sit beside the bundle, and a digest of their names
   * and contents. Additive and informational: a device fetches them by name when a screen needs one.
   */
  lazy_files: z.number().int().nonnegative().optional(),
  lazy_sha256: z
    .string()
    .regex(/^[0-9a-f]{64}$/)
    .optional(),
  /**
   * Added by the edge, never by druif: the https base under which `<version>/<file>` is served straight
   * from the bucket's custom domain. Additive: a reader that does not know it, or cannot reach it, uses
   * `/v1/catalogue/<version>/<file>` on the edge as before.
   */
  cdn_base: z.url({ protocol: /^https$/ }).optional(),
  schema_version: z.number().int().nonnegative(),
  min_reader_schema: z.number().int().nonnegative(),
  fold_version: z.number().int().nonnegative(),
});
export type CatalogueManifest = z.infer<typeof CatalogueManifest>;

/** `GET /v1/config`: switches the operator flips without a PWA release. Off unless the edge says otherwise. */
export const EdgeConfig = z.object({
  features: z.object({
    /** Label-photo identify (Workers AI on the edge, quota-limited); barcode and e-label QR scanning are never behind it. */
    labelScan: z.boolean(),
  }),
  /** The caller's label-scan allowance (device, network and signed-in user; the tightest wins), only while labelScan is on. */
  labelScanQuota: AiQuota.optional(),
});
export type EdgeConfig = z.infer<typeof EdgeConfig>;
